// Bomb the Hill's obstacles (#194): what's in the road on the way down,
// laid out from a seed so the same seed is always the same course (a daily
// seed or a replay later just picks one). Sim-free and three.js-free: the
// sim asks what you hit (`courseHit`), the renderer draws it
// (`render/hill-course.ts`). This file is the course's types, tables and
// queries; `hill-gen.ts` lays it out (`CourseGen`) and `hill-road.ts`
// keeps the stretch of it the park covers (`HillRoad`).
//
// The road is split in two along its length. One curb lane is traffic:
// cars, vans and buses driving down the hill slower than you, so you pass
// them. The rest is the street, crossed every so often by a row of parked
// vehicles, cones, bins and barriers with one way through it. Rows come
// closer together and their ways through narrower further down, and each
// way through is never further across from the last than you can carve in
// the distance between them. The traffic lane is never used by a row, so a
// row's way through is always there whatever the traffic is doing; the
// lane is a second way through when it's clear. Potholes (a fall unless
// you ollie them), gravel (no grip to carve on), low rails and pop-off
// ramps sit between and in front of the rows.
//
// Crosswalks (#196): every so often (more often further down) a zebra
// crossing takes the place of what sits between two rows, with a few people
// walking across it at their own pace, some in groups, some stopping
// halfway or turning back. They're obstacles that move: you time your carve
// through a gap between them. Each crossing's people walk to one shared
// cycle, and it's laid out so that at every moment of it there's a way
// through near the row's, so the road always has a way down.
//
// Intersections (#197): every so often (busier further down) a side street
// crosses the road between two rows, a lane of cars and vans driving across
// it each way at the lane's own speed (no buses: one's longer than the road
// is wide). The side street breaks the walls, with traffic lights on its
// corners. Its traffic loops to one shared cycle, laid out like a
// crosswalk's: at every moment of it, a rider holding a line through both
// lanes finds a way through. Cross traffic sweeps the whole road, so the
// rows give a side street clear road either side, the row before it has its
// way through as near the middle as it can, and the way through the traffic
// can be anywhere you can carve to from there.
//
// Traffic lights (#214): each side street's lights run one cycle, the road
// green then the side street green, with amber and an all-red beat between.
// Its traffic waits at its stop line, queued, pulls away on green and is
// across before the road goes green again; the road's traffic stops at red
// and queues too (`hill-traffic.ts`), so the two never meet in the middle.
// The rider needn't stop: it's a bomb run.

/** Anything you can run into, and the soft ones: a pothole throws you only on the ground, gravel only takes your grip. */
export type HillObstacleKind = "car" | "van" | "bus" | "cone" | "bin" | "barrier" | "rail" | "pothole" | "gravel";

/** The vehicles, parked or in traffic. */
export type HillVehicleKind = "car" | "van" | "bus";

/** A thing in the road: a box `hw` across (x) by `hl` along (z) either side of its centre, `h` tall from the road. */
export interface HillObstacle {
  kind: HillObstacleKind;
  x: number;
  z: number;
  hw: number;
  hl: number;
  h: number;
  /** Which paint it gets (render only), 0 .. 1. */
  tint: number;
}

/** A vehicle in the traffic lane, driving down the hill at `speed` from where it starts in its chunk (#195). */
export interface HillTraffic {
  kind: HillVehicleKind;
  x: number;
  hw: number;
  hl: number;
  h: number;
  /** m/s down the hill (+z). */
  speed: number;
  /** Its course z when the run's traffic set off (`HillCourse.t0`). */
  start: number;
  tint: number;
}

/** A ramp to pop off, rising toward +z (down the hill) from `z` over `len`. Compiled into the park as a kicker. */
export interface HillRamp {
  x: number;
  z: number;
  hw: number;
  len: number;
  h: number;
}

/**
 * A rail to grind down the hill (#237): laid along the road (z) at x, from
 * its top end at `z` `len` metres on down, `h` above the road all the way,
 * so it falls with the hill. Compiled into the park's rails (`placeRoad`);
 * riding into it on the ground is a wall like any rail's.
 */
export interface HillGrind {
  x: number;
  z: number;
  len: number;
  h: number;
}

/** A row's way through: the open stretch of road across it (x0 .. x1) at z. */
export interface HillGap {
  z: number;
  x0: number;
  x1: number;
}

/** A zebra crossing (#196) across the whole road at z, `hl` either side of it along the road. */
export interface HillCrossing {
  z: number;
  hl: number;
  /** Where its people always leave a way through (x0 .. x1 across the road): near the row's before it. */
  x0: number;
  x1: number;
}

/**
 * Someone on a crosswalk (#196). They stand at `z` and walk back and forth
 * across the road to a cycle `period` seconds long: `keys` are where they
 * are (x) at each time through it, from the run's start plus `phase`.
 */
export interface HillWalker {
  z: number;
  keys: readonly { t: number; x: number }[];
  period: number;
  phase: number;
  /** Their size: `hw` either side across and along the road, `h` tall. */
  hw: number;
  h: number;
  /** Which outfit they wear (render only), 0 .. 1. */
  tint: number;
}

/**
 * A side street's traffic lights (#214): one cycle `period` seconds long,
 * `phase` seconds into it when the run's traffic set off. The road goes
 * first: green, amber, all red; then the side street: green, amber, all
 * red, and round again.
 */
export interface HillLight {
  roadGreen: number;
  sideGreen: number;
  amber: number;
  allRed: number;
  period: number;
  phase: number;
}

/** What a light shows (#214). */
export type LightColor = "green" | "amber" | "red";

/** A side street (#197) crossing the road at z, `hl` either side of it along the road, breaking the walls there. Its way through (x0 .. x1) is wider than a crosswalk's: as far from the row's as you can carve getting there. */
export interface HillJunction {
  /** Its traffic lights (#214), which the road's traffic and its own obey (the rider needn't). */
  light: HillLight;
  z: number;
  hl: number;
  /** How far it runs off either side of the road (m), up to its far end. */
  street: number;
  /** Where its cross traffic always leaves a way through (x0 .. x1 across the road): near the row's before it. */
  x0: number;
  x1: number;
}

/**
 * A vehicle crossing the road on a side street (#197), in its lane at `z`,
 * driving `dir` along x at up to `speed`. It loops to its lights' cycle
 * (#214): `span` metres of street a `period`, from `-span / 2` to
 * `span / 2` (times `dir`), out of sight at both ends. It waits at `rest`
 * (along its way: its front at the stop line, or queued behind the ones in
 * front) until `go` seconds into the cycle, pulls away (`JUNCTION.accel`),
 * drives across the road and on round, and eases to a stop
 * (`JUNCTION.brake`) back at `rest`. `phase` is its lights' (`HillLight`).
 */
export interface HillCrosser {
  kind: HillVehicleKind;
  z: number;
  /** Its half-length along its way (x), half-width along the road (z) and roof height. */
  hl: number;
  hw: number;
  h: number;
  dir: -1 | 1;
  speed: number;
  span: number;
  period: number;
  phase: number;
  /** Where it waits for green (along its way, x times `dir`) and when in the cycle it pulls away (s). */
  rest: number;
  go: number;
  tint: number;
}

export interface HillCourse {
  /** The stretch of road the park covers (z, top to bottom); the course runs on past it (#195). */
  z0: number;
  z1: number;
  /** The road's half-width (the curbs). */
  halfWidth: number;
  /** The traffic lane: x0 .. x1 across the road. */
  lane: { x0: number; x1: number };
  /** Everything standing still, by z. */
  obstacles: HillObstacle[];
  traffic: HillTraffic[];
  ramps: HillRamp[];
  /** The rails to grind (#237), by z. */
  grinds: HillGrind[];
  gaps: HillGap[];
  /** The crosswalks (#196) and the people on them, by z. */
  crossings: HillCrossing[];
  walkers: HillWalker[];
  /** The side streets (#197) and their cross traffic, by z. */
  junctions: HillJunction[];
  crossers: HillCrosser[];
  /** How far the road has moved back up (#195): traffic drives in course z, this much further down. */
  offset: number;
  /** The sim time the run's traffic set off. */
  t0: number;
}

/** The vehicles' sizes: half-width, half-length and roof height (m). */
export const HILL_VEHICLES: Readonly<Record<HillVehicleKind, { hw: number; hl: number; h: number }>> = {
  car: { hw: 0.9, hl: 2.1, h: 1.45 },
  van: { hw: 1.0, hl: 2.6, h: 2.1 },
  bus: { hw: 1.25, hl: 5.5, h: 3.1 },
};

/** How a course is laid out: `CourseGen` takes one of these (#208), `COURSE` unless told otherwise. */
export interface CourseTuning {
  laneWidth: number;
  firstRow: number;
  spacingTop: number;
  spacingBottom: number;
  gapTop: number;
  gapBottom: number;
  shift: number;
  trafficEvery: number;
  warmup: { length: number; spacing: number; gap: number; extras: number };
  easeIn: number;
  level: number;
  swing: number;
  trafficSpeed: { min: number; max: number };
  crossingTop: number;
  crossingMore: number;
  junctionTop: number;
  junctionMore: number;
  /**
   * The tutorial's road (#201): a crosswalk after row `crossing` and a side
   * street after row `junction` (counting rows from 0), whatever the warm-up
   * says, at the gentlest the ranges go.
   */
  lessons?: { crossing: number; junction: number };
}

/** How the course is laid out (m): the traffic lane's width, the first row, rows' spacing and ways through from easiest (`Top`) to hardest (`Bottom`). */
export const COURSE = {
  laneWidth: 2.8,
  firstRow: 45,
  spacingTop: 28,
  spacingBottom: 14,
  gapTop: 3.2,
  gapBottom: 1.9,
  /** A way through moves at most this share of the distance to it across from the last. */
  shift: 0.3,
  /** Traffic: one vehicle per this many metres of course, this fast (m/s). */
  trafficEvery: 110,
  /**
   * The warm-up (#206): the first `length` metres have no crosswalks, side
   * streets or moving traffic, and its rows are `spacing` apart with ways
   * through `gap` wide, with a pothole, gravel or a rail between `extras` of them.
   */
  warmup: { length: 200, spacing: 40, gap: 4, extras: 0.15 },
  /**
   * Then over `easeIn` metres the road eases into its difficulty and holds it
   * (#206, the road is endless and no longer ever harder): `level` between
   * the top and the bottom of each range below, ± `swing` chunk to chunk.
   */
  easeIn: 100,
  level: 0.5,
  swing: 0.1,
  trafficSpeed: { min: 7, max: 9.5 },
  /** A crosswalk (#196) between two rows, this likely at the top of the range and this much more at its bottom. */
  crossingTop: 0.2,
  crossingMore: 0.3,
  /** A side street (#197) between two rows, this likely at the top of the range and this much more at its bottom. */
  junctionTop: 0.12,
  junctionMore: 0.2,
} as const satisfies CourseTuning;

/**
 * The tutorial's road (#201): the run's own warm-up (wide rows far apart, no
 * moving traffic) stretched to hold every lesson, more potholes, gravel and
 * rails to ollie, and a crosswalk and a side street laid out early on it for
 * their lessons. Past it the road eases in like any run's.
 */
export const TUTORIAL_COURSE = {
  ...COURSE,
  warmup: { ...COURSE.warmup, length: 360, extras: 0.5 },
  lessons: { crossing: 4, junction: 6 },
} as const satisfies CourseTuning;

/**
 * A side street (#197): its half-width along the road, its lanes' offset
 * from its middle, how far it runs off either side of the road (m), its
 * traffic's pace (m/s) and loop (s, top to tightest), and the rider's
 * slowest crossing, for the way through its traffic always leaves.
 */
export const JUNCTION = {
  hl: 4,
  lane: 1.9,
  street: 40,
  pace: { min: 5.5, max: 8.5 },
  /**
   * Its lights' cycle (s, #214): the road's green from the top to the
   * tightest (shorter further down), the side street's (longer further down,
   * so more of its traffic gets across), and each amber and all-red beat.
   */
  light: { roadTop: 13, roadBottom: 8, sideTop: 5, sideBottom: 7, amber: 2.5, allRed: 1.5 },
  /** How hard its traffic pulls away and eases to a stop (m/s²), how long each one waits after the one in front pulls away (s), and the gap they queue at (m). */
  accel: 2.5,
  brake: 2.5,
  react: 0.8,
  queue: 2,
  /** How far from the road's edge a side street's traffic stops: its front at the stop line (m). */
  stop: 1.6,
  /** The slowest a rider bombs through (m/s): each lane is checked swept over the time they take crossing it. */
  rider: 12,
  /** The clear road at least either side of a side street (m), to carve to a gap in its traffic. */
  approach: 14,
} as const;

/** A crosswalk (#196): its half-depth along the road, its people's size and pace (m/s), and how wide a way through they always leave (m). */
export const CROSSING = {
  hl: 1.4,
  walker: { hw: 0.25, h: 1.75 },
  pace: { min: 1.0, max: 1.7 },
  /** The way through they leave, in the rider's centre (so a gap this much wider than the rider). */
  open: 0.6,
  /** How far either side of the row's way through the crossing's own way through may be (m). */
  reach: 0.6,
  /**
   * Where it leads (#202): a dropped kerb and the pavement either side, and
   * a footpath straight off it to an alley between the buildings. People
   * wait on the pavement `kerb` m back from the road's edge, and come and go
   * along the footpath `path` m out from the road's edge.
   */
  kerb: 0.45,
  path: 4.5,
  /** How long they stand at the kerb looking both ways before they step out (s). */
  look: 1,
} as const;

/**
 * The rails to grind (#237): how likely one is between two rows at the top
 * of the range and how much more at its bottom (none in the warm-up), how
 * far it stands out from the row's way through (m, behind the row's edge),
 * how clear of anything solid it keeps (m, either side and past its end, so
 * coming off at speed lands on open road), the room after it to carve to the
 * next row's way through (m, at least), and how long and high a rail is (m).
 */
export const GRIND = {
  top: 0.3,
  more: 0.25,
  beside: 0.5,
  clear: 0.6,
  land: 4,
  runout: 6,
  len: { min: 5, max: 18 },
  h: { min: 0.45, max: 0.65 },
} as const;

/** Narrower than this (m) and nobody fits through: a row may leave such slivers. */
export const PASS_WIDTH = 0.6;

/** What one stretch of the course holds (#195), in course z (where it would be with the road never moved). */
export interface CourseChunk {
  /** Its index: it covers z0 + k * CHUNK .. z0 + (k + 1) * CHUNK, give or take what sits either side of a row. */
  k: number;
  obstacles: HillObstacle[];
  traffic: HillTraffic[];
  ramps: HillRamp[];
  grinds: HillGrind[];
  gaps: HillGap[];
  crossings: HillCrossing[];
  walkers: HillWalker[];
  junctions: HillJunction[];
  crossers: HillCrosser[];
}

/** Where a walker is across the road (x) at `t` seconds into their cycle. */
export function keyX(w: HillWalker, t: number): number {
  const k = w.keys;
  const tt = ((t % w.period) + w.period) % w.period;
  for (let i = 1; i < k.length; i++) {
    const b = k[i]!;
    if (tt <= b.t) {
      const a = k[i - 1]!;
      return b.t > a.t ? a.x + ((b.x - a.x) * (tt - a.t)) / (b.t - a.t) : b.x;
    }
  }
  return k[k.length - 1]!.x;
}

/** How often (s) a crossing's cycle is checked for its way through. */
export const CROSSING_STEP = 0.05;

/** `t` wrapped into 0 .. `period`. */
const wrap = (t: number, period: number) => ((t % period) + period) % period;

/** How far into its cycle a side street's lights (#214) are at `t` seconds into the run's traffic. */
export function lightTime(l: HillLight, t: number): number {
  return wrap(t + l.phase, l.period);
}

/** When in its cycle a side street's lights (#214) go green for the side street (s): after the road's green, amber and all red. */
export function sideStart(l: HillLight): number {
  return l.roadGreen + l.amber + l.allRed;
}

/** What a side street's lights (#214) show the road's traffic (and the rider) at `t` seconds into the run's traffic. */
export function roadLight(l: HillLight, t: number): LightColor {
  const c = lightTime(l, t);
  return c < l.roadGreen ? "green" : c < l.roadGreen + l.amber ? "amber" : "red";
}

/** What a side street's lights (#214) show its own traffic at `t` seconds into the run's traffic. */
export function sideLight(l: HillLight, t: number): LightColor {
  const c = lightTime(l, t) - sideStart(l);
  return c >= 0 && c < l.sideGreen ? "green" : c >= l.sideGreen && c < l.sideGreen + l.amber ? "amber" : "red";
}

/** How long (s) from `t` (into the run's traffic) until a side street's lights (#214) next go green for the side street (0 while they are). */
export function untilSideGreen(l: HillLight, t: number): number {
  const c = lightTime(l, t) - sideStart(l);
  return c >= 0 && c < l.sideGreen ? 0 : wrap(-c, l.period);
}

/**
 * How far (m) something driving at up to `v`, pulling away at `JUNCTION.accel`
 * from standing and easing to a stop at `JUNCTION.brake` `span` metres on,
 * has gone `t` seconds after pulling away (#214): `span` once it's stopped.
 */
export function driven(v: number, span: number, t: number): number {
  const a = JUNCTION.accel;
  const b = JUNCTION.brake;
  const ta = v / a;
  const da = (v * v) / (2 * a);
  const db = (v * v) / (2 * b);
  const cruise = (span - da - db) / v;
  if (t <= 0) return 0;
  if (t < ta) return (a * t * t) / 2;
  if (t < ta + cruise) return da + v * (t - ta);
  const e = t - ta - cruise;
  if (e < v / b) return span - db + v * e - (b * e * e) / 2;
  return span;
}

/** How long (s) one drive of `span` metres at up to `v` takes from standing to standing (#214): see `driven`. */
export function driveTime(v: number, span: number): number {
  return span / v + v / (2 * JUNCTION.accel) + v / (2 * JUNCTION.brake);
}

/** Where a side street's vehicle (#197) is along its way (x) at `t` seconds into the run's traffic: waiting at its stop, or driving round its loop (#214). */
export function loopX(c: HillCrosser, t: number): number {
  const s = driven(c.speed, c.span, wrap(t + c.phase - c.go, c.period));
  return c.dir * (wrap(c.rest + s + c.span / 2, c.span) - c.span / 2);
}

/** A stretch of road (m): the course is laid out, kept and drawn this much at a time, and the road moves back up by it (#195). */
export const CHUNK = 60;

/**
 * How the road's traffic drives (#214): where a vehicle is (course z) `t`
 * seconds into the run's traffic, stopping at red lights and queueing.
 * `HillRoad` keeps one (`hill-traffic.ts`) and hands its vehicles to it here.
 */
export interface TrafficDriver {
  z(v: HillTraffic, t: number): number;
}

/** Which driver drives each traffic vehicle (#214): one on its own drives straight on at its speed. */
export const TRAFFIC_DRIVERS = new WeakMap<HillTraffic, TrafficDriver>();

/** Where a traffic vehicle is at sim time `clock` (its z in the park now; x is its lane). */
export function trafficZ(course: HillCourse, v: HillTraffic, clock: number): number {
  const t = clock - course.t0;
  const d = TRAFFIC_DRIVERS.get(v);
  return (d ? d.z(v, t) : v.start + v.speed * t) - course.offset;
}

/** Where someone on a crosswalk (#196) is across the road (x) at sim time `clock`. */
export function walkerX(course: HillCourse, w: HillWalker, clock: number): number {
  return keyX(w, clock - course.t0 + w.phase);
}

/** Which way someone on a crosswalk is walking at `clock` (#196): -1 or 1 across the road, 0 standing. */
export function walkerDir(course: HillCourse, w: HillWalker, clock: number): -1 | 0 | 1 {
  const d = walkerX(course, w, clock + 0.05) - walkerX(course, w, clock);
  return d > 1e-4 ? 1 : d < -1e-4 ? -1 : 0;
}

/** Where a side street's vehicle (#197) is along its way across the road (x) at sim time `clock`. */
export function crosserX(course: HillCourse, c: HillCrosser, clock: number): number {
  return loopX(c, clock - course.t0);
}

/** The side street (#197) at z, if (x, z) is on it off the side of the road: no wall there. */
export function sideStreetAt(course: HillCourse, z: number): HillJunction | null {
  for (const j of course.junctions) if (Math.abs(z - j.z) <= j.hl) return j;
  return null;
}

/**
 * A side street's vehicle (#197) about to cross the rider's line: the
 * first one in a side street from `ahead` metres down the road, driving
 * toward x and due there within `HORN_WITHIN` seconds of the rider at
 * `speed`, or on it already. It honks.
 */
export function hornAhead(course: HillCourse, x: number, z: number, speed: number, clock: number, ahead: number = HORN_AHEAD): HillCrosser | null {
  for (const c of course.crossers) {
    if (c.z < z) continue;
    if (c.z > z + ahead) break;
    const eta = (c.z - z) / Math.max(speed, 1);
    const at = crosserX(course, c, clock + eta);
    // Heading for the rider's line (or on it) when the rider gets there.
    const toward = (x - at) * c.dir;
    if (toward > -(c.hl + RIDER_R) && toward < c.hl + RIDER_R + c.speed * HORN_WITHIN) return c;
  }
  return null;
}

/** A side street's vehicle (#197) honks when it'll reach the rider's line this soon (s) after them, from this far up the road (m). */
export const HORN_WITHIN = 1;
export const HORN_AHEAD = 22;

/**
 * The side street's vehicles (#197) the rider just whipped past close,
 * going from `zFrom` to `zTo` at x: each counted once, as its lane goes by.
 */
export function closeShaves(course: HillCourse, x: number, zFrom: number, zTo: number, clock: number): HillCrosser[] {
  const out: HillCrosser[] = [];
  if (zTo <= zFrom) return out;
  for (const c of course.crossers) {
    if (c.z > zTo) break;
    if (c.z <= zFrom) continue;
    if (Math.abs(x - crosserX(course, c, clock)) <= c.hl + RIDER_R + CLOSE_CALL) out.push(c);
  }
  return out;
}

/** The skater's size against an obstacle's box (m). */
export const RIDER_R = 0.3;

/** Anything a run down the hill can end on in the road: what's standing in it, a vehicle, or someone crossing it (#196). */
export type HillHitKind = HillObstacleKind | "pedestrian";

/** Passing someone on a crosswalk this close (m, between you and them) without hitting them is a close call (#196). */
export const CLOSE_CALL = 1.0;

/**
 * The people on a crosswalk (#196) the rider just passed close by, going
 * from `zFrom` to `zTo` at x: each counted once, as their z goes by.
 */
export function closeCalls(course: HillCourse, x: number, zFrom: number, zTo: number, clock: number): HillWalker[] {
  const out: HillWalker[] = [];
  if (zTo <= zFrom) return out;
  for (const w of course.walkers) {
    if (w.z > zTo) break;
    if (w.z <= zFrom) continue;
    const wx = walkerX(course, w, clock);
    // Off the road (on the pavement or the footpath, #202) isn't passing them on the crosswalk.
    if (Math.abs(wx) < course.halfWidth && Math.abs(x - wx) <= w.hw + RIDER_R + CLOSE_CALL) out.push(w);
  }
  return out;
}

/**
 * What a rider at (x, z), `up` metres above the road, has run into at
 * `clock`, if anything: a solid obstacle or a vehicle they're not clear
 * above, or a pothole under their wheels while `grounded`.
 */
export function courseHit(course: HillCourse, x: number, z: number, up: number, grounded: boolean, clock: number): HillHitKind | null {
  for (const o of course.obstacles) {
    if (o.z - o.hl - RIDER_R > z) break;
    if (o.z + o.hl + RIDER_R < z || o.kind === "gravel") continue;
    if (o.kind === "pothole") {
      if (grounded && up < 0.2 && Math.abs(x - o.x) <= o.hw && Math.abs(z - o.z) <= o.hl) return "pothole";
      continue;
    }
    if (up < o.h && Math.abs(x - o.x) <= o.hw + RIDER_R) return o.kind;
  }
  for (const v of course.traffic) {
    if (up >= v.h || Math.abs(x - v.x) > v.hw + RIDER_R) continue;
    if (Math.abs(z - trafficZ(course, v, clock)) <= v.hl + RIDER_R) return v.kind;
  }
  for (const w of course.walkers) {
    if (w.z - w.hw - RIDER_R > z) break;
    if (w.z + w.hw + RIDER_R < z || up >= w.h) continue;
    const wx = walkerX(course, w, clock);
    // Waiting on the pavement or walking the footpath (#202), behind the kerb: out of reach.
    if (Math.abs(wx) < course.halfWidth && Math.abs(x - wx) <= w.hw + RIDER_R) return "pedestrian";
  }
  for (const c of course.crossers) {
    if (c.z - c.hw - RIDER_R > z) break;
    if (c.z + c.hw + RIDER_R < z || up >= c.h) continue;
    if (Math.abs(x - crosserX(course, c, clock)) <= c.hl + RIDER_R) return c.kind;
  }
  return null;
}

/** Whether (x, z) is on a gravel patch (#194): no grip to carve on. */
export function onGravel(course: HillCourse, x: number, z: number): boolean {
  for (const o of course.obstacles) {
    if (o.z - o.hl > z) break;
    if (o.kind === "gravel" && Math.abs(z - o.z) <= o.hl && Math.abs(x - o.x) <= o.hw) return true;
  }
  return false;
}
